import { useEffect, useState } from 'react';
import LeaderboardService, { LeaderboardScope, PublicLeaderboardResponse } from '../../application/services/LeaderboardService';

interface LeaderboardState {
  isLoading: boolean;
  error: string | null;
  board: PublicLeaderboardResponse | null;
}

/**
 * Fetches one page of a leaderboard scope. `scopeId` is `null` for GLOBAL and for a COUNTRY/CITY
 * scope not yet resolved (e.g. still waiting on useLeaderboardScopes to resolve a :countryCode/
 * :citySlug route param) — the fetch is held off until resolution finishes rather than firing a
 * request with an incomplete scope.
 */
export function useLeaderboard(scope: LeaderboardScope, scopeId: number | null, page: number, ready = true): LeaderboardState {
  const [state, setState] = useState<LeaderboardState>({ isLoading: true, error: null, board: null });

  useEffect(() => {
    if (!ready) {
      return;
    }
    let cancelled = false;
    setState((prev) => ({ ...prev, isLoading: true, error: null }));
    (async () => {
      try {
        const board = await LeaderboardService.getLeaderboard(scope, scopeId ?? undefined, page);
        if (!cancelled) {
          setState({ isLoading: false, error: null, board });
        }
      } catch (err) {
        if (!cancelled) {
          setState({ isLoading: false, error: err instanceof Error ? err.message : 'Failed to load the leaderboard', board: null });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [scope, scopeId, page, ready]);

  return state;
}
